/**
 * One figure, stated plainly. The value renders immediately (no count-up). A
 * delta always names the period it compares against. A caveat line says what
 * the number leaves out. A status, when given, is a marker + words, never
 * colour alone.
 */
import React from 'react';
import { Marker } from './Marker';
import { STATUS, STATUS_MARKER, StatusKey } from './tokens';

export interface MetricDelta {
  /** Display text for the change, e.g. "+12" or "-3%". */
  value: string;
  /** The comparison period, e.g. "vs the previous 30 days". Required: a delta without one means nothing. */
  period: string;
  /** Whether this change is good news; omit when it is neither (a larger backlog is not a win). */
  favorable?: boolean;
}

export interface MetricTileProps {
  label: string;
  value: React.ReactNode;
  delta?: MetricDelta;
  caveat?: React.ReactNode;
  status?: StatusKey;
  /** Words for the status; defaults to the token's label. */
  statusText?: string;
  /** Category colour for the top rule (a CSS colour or var). */
  accent?: string;
  /** Larger, brand-soft tile for the lead figure. */
  feature?: boolean;
  /** Extra content under the value (a split bar, a breakdown). */
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export default function MetricTile({
  label, value, delta, caveat, status, statusText, accent, feature = false, children, className = '', ...rest
}: MetricTileProps) {
  const style = accent ? ({ '--tide-metric-accent': accent } as React.CSSProperties) : undefined;
  return (
    <div
      className={`tide-metric ${feature ? 'tide-metric--feature' : ''} ${className}`.trim()}
      style={style}
      data-testid={rest['data-testid']}
    >
      <div className="tide-metric__label">{label}</div>
      <div className="tide-metric__value">{value}</div>
      {delta && (
        <div className="tide-metric__delta">
          {delta.value} {delta.period}
          {delta.favorable !== undefined && <span className="tide-sr">{delta.favorable ? ' (an improvement)' : ' (a deterioration)'}</span>}
        </div>
      )}
      {status && (
        <span className="tide-metric__status" style={{ color: STATUS[status].fg, background: STATUS[status].bg }}>
          <Marker shape={STATUS_MARKER[status]} color={STATUS[status].fg} size={10} />
          {statusText || STATUS[status].label}
        </span>
      )}
      {caveat && <div className="tide-metric__caveat">{caveat}</div>}
      {children && <div className="tide-metric__extra">{children}</div>}
    </div>
  );
}
